import { DEFAULT_BOUNDARY_COUNT, DEFAULT_SIBLING_COUNT } from "./constants";
import type { PaginationFitMetrics } from "./usePaginationFit";

export const ELLIPSIS = "ellipsis" as const;

export type PaginationRangeItem = number | typeof ELLIPSIS;

type GetPaginationRangeOptions = {
  page: number;
  pageCount: number;
  siblingCount?: number;
  boundaryCount?: number;
};

function range(start: number, end: number): number[] {
  return Array.from(
    { length: Math.max(end - start + 1, 0) },
    (_, index) => start + index,
  );
}

export function getPaginationRange({
  page,
  pageCount,
  siblingCount = DEFAULT_SIBLING_COUNT,
  boundaryCount = DEFAULT_BOUNDARY_COUNT,
}: GetPaginationRangeOptions): PaginationRangeItem[] {
  if (pageCount <= 0) {
    return [];
  }

  const startPages = range(1, Math.min(boundaryCount, pageCount));
  const endPages = range(
    Math.max(pageCount - boundaryCount + 1, boundaryCount + 1),
    pageCount,
  );

  const siblingsStart = Math.max(
    Math.min(
      page - siblingCount,
      pageCount - boundaryCount - siblingCount * 2 - 1,
    ),
    boundaryCount + 2,
  );

  const siblingsEnd = Math.min(
    Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2),
    endPages.length > 0 ? endPages[0] - 2 : pageCount - 1,
  );

  return [
    ...startPages,
    ...(siblingsStart > boundaryCount + 2
      ? [ELLIPSIS]
      : boundaryCount + 1 < pageCount - boundaryCount
        ? [boundaryCount + 1]
        : []),
    ...range(siblingsStart, siblingsEnd),
    ...(siblingsEnd < pageCount - boundaryCount - 1
      ? [ELLIPSIS]
      : pageCount - boundaryCount > boundaryCount
        ? [pageCount - boundaryCount]
        : []),
    ...endPages,
  ];
}

type FitPaginationRangeOptions = GetPaginationRangeOptions & {
  metrics: PaginationFitMetrics | null;
};

function rangeWidth(
  range: PaginationRangeItem[],
  { pageWidth, ellipsisWidth, gap }: PaginationFitMetrics,
): number {
  const items = range.reduce<number>(
    (total, item) => total + (item === ELLIPSIS ? ellipsisWidth : pageWidth),
    0,
  );

  return items + Math.max(range.length - 1, 0) * gap;
}

export function fitPaginationRange({
  page,
  pageCount,
  siblingCount = DEFAULT_SIBLING_COUNT,
  boundaryCount = DEFAULT_BOUNDARY_COUNT,
  metrics,
}: FitPaginationRangeOptions): PaginationRangeItem[] {
  const build = (siblings: number, boundaries: number) =>
    getPaginationRange({
      page,
      pageCount,
      siblingCount: siblings,
      boundaryCount: boundaries,
    });

  let siblings = siblingCount;
  let boundaries = boundaryCount;
  let range = build(siblings, boundaries);

  if (metrics === null || range.length === 0) {
    return range;
  }

  while (rangeWidth(range, metrics) > metrics.available) {
    if (siblings === 0 && boundaries === 0) {
      // Nothing left to give up but the ellipses, and then the range is minimal.
      const withoutEllipsis = range.filter((item) => item !== ELLIPSIS);

      return withoutEllipsis.length > 0 ? withoutEllipsis : range;
    }

    if (siblings > 0) {
      siblings -= 1;
    } else {
      boundaries -= 1;
    }

    range = build(siblings, boundaries);
  }

  return range;
}
